import React, { useState, useMemo, useCallback, useEffect } from 'react';
import SalesFileContext from './SalesFileContext';
import { useLoading } from '@/utils/LoadingUtil';
import salesFileModel from '@/model/sales-rep/SalesFile.model';
import {
  uploadSalesFile,
  getSalesFiles,
  getWhrehouseList,
} from '@/services/SalesFileService';
import { showAlert } from '@/components';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '@/lib/handleApiError';

const SalesFileContextProvider = ({ children }) => {
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [error, setError] = useState(null);
  const { salesFile, setSalesFile, initialState } = salesFileModel();
  const [salesFileData, setSalesFileData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [warehouseList, setWarehouseList] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const nav = useNavigate();

  useEffect(() => {
    setCurrentPage(0);
    setSearchQuery('');
    setPageSize(100);
    setSortBy('');
    setSortDir('desc');
  }, [nav]);

  const uploadSalesFileAPI = async (file) => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append('file', file);
      Object.keys(salesFile).forEach((key) => {
        formData.append(key, salesFile[key]);
      });
      const response = await uploadSalesFile(formData);
      showAlert('success', response.data || 'File uploaded successfully');
      getSalesFilesAPI();
    } catch (error) {
      handleApiError('error', error.response.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  };

  const getSalesFilesAPI = useCallback(
    async (
      page = currentPage,
      size = pageSize,
      search = searchQuery,
      sortField = sortBy, // Renamed parameter to avoid shadowing
      sortDirection = sortDir, // Renamed parameter to avoid shadowing
    ) => {
      startLoading();
      try {
        const data = await getSalesFiles(
          page,
          size,
          search,
          sortField, // Use renamed parameter
          sortDirection, // Use renamed parameter
        );
        setSalesFileData(data?._embedded?.salesFilesDTOList || data);
        setTotalElements(data?.page?.totalElements);
      } catch (err) {
        setError(err.message);
        setSalesFileData([]);
        setTotalElements(0);
        handleApiError('error', error.response.data, error?.response?.status);
      } finally {
        stopLoading();
      }
    },
    [currentPage, pageSize, searchQuery, startLoading, stopLoading],
  );

  const getWhrehouseListAPI = useCallback(async () => {
    startLoading();
    try {
      const data = await getWhrehouseList();
      setWarehouseList(data.whseList || []);
    } catch (err) {
      setError(err.message);
      setWarehouseList([]);
      handleApiError('error', error.response.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      isLoading,
      startLoading,
      stopLoading,
      error,
      setError,
      salesFile,
      setSalesFile,
      initialState,
      uploadSalesFileAPI,
      getSalesFilesAPI,
      setSalesFileData,
      salesFileData,
      totalElements,
      currentPage,
      pageSize,
      searchQuery,
      setCurrentPage,
      setPageSize,
      setSearchQuery,
      warehouseList,
      getWhrehouseListAPI,
      sortBy,
      setSortBy,
      sortDir,
      setSortDir,
    }),
    [
      isLoading,
      startLoading,
      stopLoading,
      error,
      salesFile,
      salesFileData,
      totalElements,
      currentPage,
      pageSize,
      searchQuery,
      getSalesFilesAPI,
      warehouseList,
      getWhrehouseListAPI,
      sortBy,
      setSortBy,
      sortDir,
      setSortDir,
    ],
  );

  return (
    <SalesFileContext.Provider value={contextValue}>
      {children}
    </SalesFileContext.Provider>
  );
};

export default SalesFileContextProvider;
export const useSalesFileContext = () => React.useContext(SalesFileContext);
